import { useEffect } from 'react';
import { Header, Link } from '@amzn/awsui-components-react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { AppOutletContext } from '../App';
import { NonProfitSearchHelpPanel } from '../components/nonprofits/nonProfitSearchHelpPanel';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { useLocalizedText } from '../hooks/localizedText';
import { NAVIGATION_REDESIGN, NON_PROFIT_SEARCH } from '../utils/constants';

const I18N = 'nonProfitSearch';

export function NonProfitSearch() {
  const { t } = useLocalizedText(['nonprofits', 'translation']);
  const outletContext = useOutletContext<AppOutletContext | null>();

  // Leave the tools slot as it was found: it belongs to the shared AppLayout, so a panel left behind
  // would open on whichever page the user goes to next.
  useEffect(() => {
    return () => {
      outletContext?.setTools(null);
      outletContext?.setToolsOpen(false);
    };
  }, [outletContext]);

  return (
    <div data-testid="nonprofit-search-page">
      <Header
        variant="h1"
        description={t(`${I18N}.subtitle`)}
        info={
          <Link
            variant="info"
            data-testid="nonprofit-search-info-link"
            data-aci-analytics-name="nonProfitSearch_infoOpened"
            onFollow={() => {
              // One-way open: the AppLayout's own close button handles closing. Toggling here
              // instead would let local state disagree with the layout's toolsOpen.
              outletContext?.setTools(<NonProfitSearchHelpPanel />);
              outletContext?.setToolsOpen(true);
            }}
          >
            {t(`${I18N}.infoLink`)}
          </Link>
        }
      >
        {t(`${I18N}.title`)}
      </Header>
    </div>
  );
}

/**
 * Both flags gate this page. NonProfitSearch is the feature's own flag; NavigationRedesign is
 * required because the page has no shell of its own — its breadcrumbs and help panel are slots on
 * the redesigned AppLayout, and the left-nav entry that reaches it exists only in that navigation.
 */
export function NonProfitSearchView() {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  if (
    featureIsEnabled(NON_PROFIT_SEARCH, features, searchParams) &&
    featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams)
  ) {
    return <NonProfitSearch />;
  }
  return <></>;
}
